import type { CSSProperties, ReactNode } from "react";

/**
 * A SKIN is the developer-authored look a CMS-authored form is poured into.
 *
 * ─── Three layers, three owners ─────────────────────────────────────────────────────
 *   1. WHICH QUESTIONS are asked   → `field_schema`, authored in the Form Builder (data)
 *   2. HOW IT IS BUILT            → the skin's structure and typography (code)
 *   3. WHAT IT SAYS AND ITS COLOURS → the skin's SETTINGS, authored in the CMS (data)
 *
 * Layer 3 is what makes a skin usable in the same way `lead_drop_form` /
 * `lead_drop_popup` already are. A developer decides that Skin 3000 has a centred header,
 * a full-width CTA and a consent footnote; the CMS author decides what that header says,
 * what colour the CTA is, and which links the consent copy points at. Neither can do the
 * other's job, and that is the point — R16 asks for a premium design, and typography and
 * spacing stay premium only while a developer owns them.
 *
 * ─── Why a class bundle and not a whole component ───────────────────────────────────
 * A skin could have been "a component that renders the form", but then every skin would
 * re-implement step sequencing, rule evaluation, the OTP gate, answer pruning and error
 * handling — and skin #2 would drift from skin #1 on exactly the edge cases that matter.
 * `ConfigLeadForm` keeps ALL behaviour; a skin contributes appearance and its knobs.
 * Adding a skin is then a styling exercise that cannot introduce a logic bug.
 */

/** How a setting is edited in the CMS. Mirrors the control types Form_1 already uses. */
export type LeadFormSkinSettingType =
  | "text"
  | "textarea"
  | "color"
  /** A link the visitor follows — terms, privacy, a success destination. Typed by hand. */
  | "url"
  /**
   * An image. Rendered as an UPLOAD, never a URL box.
   *
   * A pasted image URL is a standing bet that someone else's server keeps serving that
   * file, over https, with permissive CORS. When the bet fails the form does not error —
   * it renders a blank hero rail to a visitor mid-enquiry, and nobody finds out. Uploading
   * puts the file on our bucket. The stored VALUE is still a URL, so nothing downstream
   * changes; only the way an author produces it does.
   */
  | "image"
  | "toggle"
  | "datetime";

/**
 * Which column a setting is persisted to.
 *
 * The split is not cosmetic: `copy_config` and `theme_config` already exist on
 * `lead_form_config` and already mean "words" and "look". Keeping words in one and
 * appearance in the other means a future "translate this form" or "restyle this form"
 * feature has a column to work on, instead of one undifferentiated blob.
 */
export type LeadFormSkinSettingStore = "copy" | "theme";

export interface LeadFormSkinSetting {
  /** Canonical key — see `LeadFormSkinValues`. Shared across skins on purpose. */
  key: string;
  label: string;
  type: LeadFormSkinSettingType;
  store: LeadFormSkinSettingStore;
  /** Used when the author has not set a value. Never undefined for a colour. */
  defaultValue?: string | boolean;
  placeholder?: string;
  /** One line under the control. Say what it AFFECTS, not what it is. */
  help?: string;
  /**
   * Surfaces this setting actually does something on. Omitted = all of them.
   *
   * The popup hero is the case this exists for: `LeadFormShell` only draws the image rail
   * on the popup surface, so on an inline form the upload control appeared, accepted a
   * file, saved it, and changed nothing anywhere — indistinguishable from a broken
   * upload. A knob that cannot affect the thing being edited should not be on screen.
   *
   * Use it sparingly. Most settings are surface-independent, and hiding one that merely
   * LOOKS unused on a surface just makes it unreachable.
   */
  surfaces?: ("fixed" | "popup")[];
}

export interface LeadFormSkinSettingGroup {
  title: string;
  settings: LeadFormSkinSetting[];
}

/**
 * The canonical knob set.
 *
 * Deliberately SHARED across skins rather than per-skin: `ConfigLeadForm` reads these
 * keys, so a new skin that reuses them needs no renderer change at all. A skin declares
 * which subset it honours by listing them in `settingGroups` — an unlisted key simply is
 * not offered to the author.
 *
 * Names follow the legacy `lead_drop_form` columns (`form_title`, `button_step_1_text`, …)
 * so anyone who has configured a lead form variant already knows this vocabulary.
 */
export interface LeadFormSkinValues {
  /* Header */
  form_title?: string;
  form_title_color?: string;
  form_sub_text?: string;
  form_sub_text_color?: string;

  /* Card + fields */
  card_background_color?: string;
  card_border_color?: string;
  field_border_color?: string;
  field_text_color?: string;
  label_color?: string;

  /* Primary CTA (step 1..n-1 and final submit) */
  button_background_color?: string;
  button_text_color?: string;
  button_step_1_text?: string;
  submit_text?: string;
  skip_text?: string;

  /* ── OTP step ─────────────────────────────────────────────────────────────────────
   * Named after the `lead_drop_form` columns lead form 9 already uses, because the two
   * draw the SAME screen: a sentence naming the masked number with an inline "Change
   * Number", a heading above four single-digit boxes, and a resend SENTENCE — not a
   * button. An author who has configured variant 9 already knows these keys. */
  otp_description_text?: string;
  otp_description_text_color?: string;
  edit_number_text?: string;
  edit_number_text_color?: string;
  otp_header_text?: string;
  otp_header_text_color?: string;
  /** The words BEFORE the resend link. The link is part of the same sentence. */
  resend_prefix_text?: string;
  resend_otp_text?: string;
  resend_otp_text_color?: string;
  button_step_2_text?: string;
  button_step_2_background_color?: string;
  button_step_2_text_color?: string;

  /* Consent */
  checkbox_description?: string;
  checkbox_description_color?: string;
  checkbox_color?: string;
  terms_conditions_link?: string;
  privacy_policy_link?: string;

  /* ── Terminal screens ─────────────────────────────────────────────────────────────
   * Three of them, mirroring the legacy variants, because "the form finished" is not one
   * outcome. A first-time submit, a returning visitor who is already registered, and a
   * brochure download each need different words — telling a returning user "thank you for
   * registering" is wrong, and a download screen has to explain that a file is coming.
   * Key names follow the legacy `lead_drop_form` columns so the vocabulary is familiar. */

  /* New user — the ordinary success screen */
  new_user_title?: string;
  new_user_subtitle?: string;
  new_user_description?: string;
  new_user_button_text?: string;
  new_user_title_color?: string;
  new_user_subtitle_color?: string;
  new_user_description_color?: string;
  new_user_background_image?: string;

  new_user_button_text_color?: string;

  /* Returning user — verified a mobile that was already registered */
  existing_user_title?: string;
  existing_user_subtitle?: string;
  existing_user_description?: string;
  existing_user_button_text?: string;
  existing_user_title_color?: string;
  existing_user_subtitle_color?: string;
  existing_user_description_color?: string;
  existing_user_background_image?: string;
  existing_user_button_text_color?: string;

  /* Brochure download — the file starts automatically; {{...}} is the manual link */
  download_title?: string;
  download_subtitle?: string;
  download_description?: string;
  download_title_color?: string;
  download_subtitle_color?: string;
  download_description_color?: string;
  download_background_image?: string;
  brochure_link_color?: string;

  /**
   * Where the terminal CTA goes. Blank closes the popup (or returns to the site root),
   * which is what "Back to Website" means on an embedded form with nothing to close.
   */
  /**
   * The brochure ending's CTA. Absent means NO button — some designs end at the file
   * itself. Declared per skin rather than inherited from the new-user ending, so each
   * design decides for itself and a change to one does not move the other.
   */
  download_button_text?: string;
  download_button_text_color?: string;

  success_button_link?: string;

  /**
   * Colour for `{course}` where it appears in an ending's title, subtitle or description.
   *
   * Its own knob rather than the surrounding text's: variant 8 sets the programme in an
   * accent while the sentence around it stays dark, which is the whole reason the token
   * renders as a span instead of being substituted into the string.
   */
  course_text_color?: string;

  /**
   * The disc behind the success tick on the ending screens.
   *
   * A setting rather than a constant: green reads as "this worked", but a university whose
   * brand IS green needs to move it, and a hardcoded tick is the one thing they cannot
   * work around.
   */
  success_badge_color?: string;

  /**
   * Seconds before the ending sends the visitor to the success destination on its own.
   *
   * Blank or 0 means no countdown — the button stays manual. Only ever runs when there IS
   * a destination, so it cannot navigate a visitor to nowhere.
   */
  success_redirect_seconds?: string | number;

  /* ── Post-submit application redirect ─────────────────────────────────────────────
   * Point the SUCCESS BUTTON at the application page for the course the visitor picked,
   * instead of at one fixed destination. Off unless the skin offers it and the author
   * turns it on — a form that collects no course has nowhere to send anyone. */
  enable_application_redirect?: boolean;
  /**
   * A path template. `{slug}` is replaced with the selected course's POK, so
   * `/application/{slug}` becomes `/application/POK1`.
   */
  application_redirect_path?: string;
  /** Skins that paint a decoration behind the ending card read this. See `renderBackdrop`. */
  success_circle_color?: string;

  /* Progress */
  show_step_indicator?: boolean;

  /**
   * Embedded-only. The inline card and the popup sheet are different objects with
   * different fills — variant 8's inline card is lavender while its popup is white — so one
   * `card_background_color` cannot describe both. Skins that do not offer this simply fall
   * back to `card_background_color`, which is what every skin did before it existed.
   */
  embedded_background_color?: string;

  /* ── Variant-8 vocabulary ─────────────────────────────────────────────────────────
   * The `lead_drop_popup` column names lead form 8 is configured with today. A skin that
   * reproduces variant 8 declares THESE as its settings so the CMS looks the way an author
   * already knows it, and maps them onto the canonical keys above via `normalizeValues`.
   * They are optional and unused by any other skin. */
  button_color?: string;
  signup_button_text?: string;
  signup_icon?: string;
  get_otp_button_text?: string;
  verify_otp_button_text?: string;
  get_otp_enabled_color?: string;
  get_otp_disabled_color?: string;
  /** The fill behind the phone row and every other field — variant 8's fields are borderless. */
  phone_row_background_color?: string;
  otp_title_text?: string;
  otp_title_color?: string;
  edit_text_color?: string;
  resend_text_color?: string;
  desktop_bg_image?: string;
  mobile_bg_image?: string;
  bg_image_alt?: string;
  /** Painted behind the desktop hero rail, so a transparent PNG sits on a colour. */
  desktop_image_bg_color?: string;
  brochure_title?: string;
  brochure_description?: string;
  brochure_title_color?: string;
  brochure_description_color?: string;
  brochure_desktop_image?: string;
  new_user_desktop_image?: string;
  existing_user_desktop_image?: string;
  success_button_text_with_pok?: string;
  success_button_text_without_pok?: string;

  /* Popup-only. The embedded surface has no image rail, so these are ignored there. */
  popup_desktop_image?: string;
  popup_mobile_image?: string;
  popup_image_alt?: string;
}

/**
 * Structural classes. NOT author-editable — this is the part a developer owns.
 * Colour is left to CSS custom properties so a settings change never needs a class change.
 *
 * ─── Popup and embedded are DIFFERENT LAYOUTS, not one layout at two widths ─────────
 * Lead form 1 is the proof: embedded (`LeaddropForm1`) is a single ~360–544px column
 * card, while the popup (`Leaddrop1`) is a ~700–780px two-column shell with a full-height
 * image rail beside the fields, its own fixed heights, a larger radius and a close button.
 * Treating the popup as "the card, but wider" produces a stretched embedded form floating
 * in a dialog — which is exactly what a first pass at this looked like.
 *
 * So a skin declares both, and `ConfigLeadForm` picks the whole tree by surface.
 */
export interface LeadFormSkinClasses {
  /* ── Shared: typography, fields, actions ─────────────────────────────────────── */
  header: string;
  /**
   * Added to `header` only on the popup surface, and only when an image is configured.
   *
   * Opting in means "my title belongs ON the image strip, not above the fields" — the
   * IITKGP sheet sets its heading in white over the arc, which is where that design's
   * breathing room comes from. Omit it and the header stays in normal flow, which is what
   * a skin with a side rail wants: skin 3000's title sits beside its image, not on it.
   *
   * The condition is not decoration. The class positions the header absolutely, so with
   * no image — and therefore no strip — it would land on top of the first field instead.
   */
  headerOnAside?: string;
  title: string;
  subtitle: string;
  /** The grid wrapper around the fields. Owns row/column gaps. */
  body: string;
  label: string;
  requiredMark: string;
  /** Base input/select/textarea. */
  field: string;
  /** Added to `field` when that field has a server or client error. */
  fieldInvalid: string;
  errorText: string;
  helpText: string;
  /** Wrapper around the CTA row. */
  actions: string;
  button: string;
  buttonDisabled: string;
  secondaryButton: string;
  consent: string;
  consentLink: string;
  /* ── OTP step ────────────────────────────────────────────────────────────────
   * Structured after lead form 9's gate, which is not "a code box and a resend button":
   * it is two sentences with links inside them, wrapped around a row of single-digit
   * boxes. A skin that only exposed `otpInput` could not express it. */
  /** Column wrapper for the whole OTP body. */
  otpBlock: string;
  /** "We've sent a 4-digit OTP to +91 …" — carries the Change Number link inline. */
  otpDescription: string;
  /** An underlined action INSIDE a sentence: Change Number, Resend OTP. */
  otpInlineAction: string;
  /** "Enter 4-digit OTP Here". */
  otpHeader: string;
  /** Row holding the single-digit boxes. */
  otpBoxes: string;
  /** One digit box. */
  otpBox: string;
  /** Added to `otpBox` while the entered code was rejected. */
  otpBoxInvalid: string;
  /** "Didn't receive the OTP? Resend OTP in 30 sec" — a sentence, not a button. */
  otpResend: string;
  /** Form-level error banner. */
  banner: string;
  /* ── Terminal screen ─────────────────────────────────────────────────────────
   * The ending REPLACES the form card rather than rendering inside it. Lead form 9's
   * last screen has its own padding, its own minimum height and a decoration that
   * bleeds past the top edge; nesting it in the form card gave a card inside a card. */
  successCard: string;
  /** Added to `successCard` on the popup surface — the width ladder lives here. */
  successCardPopup: string;
  /**
   * The ending's own wrapper when it renders INSIDE the shell
   * (`endingKeepsShellChrome`). Contributes layout only — no card, no width, no scroll,
   * because the shell already owns all three.
   */
  successContentBare: string;
  /** "Redirecting in 7 Sec" under the ending CTA. Rendered only while a countdown runs. */
  successCountdown: string;
  successImage: string;
  /** Wrapper around the words and the CTA. Owns their vertical rhythm. */
  successContent: string;
  /** Title + subtitle pair, tight against each other. */
  successHeadings: string;
  successTitle: string;
  successSubtitle: string;
  successBody: string;
  /** The outlined pill CTA. Its colour comes from the per-outcome setting. */
  successButton: string;
  /** Small "Step 2 of 3" text. */
  stepMeta: string;
  radioLabel: string;

  /* ── Embedded surface ────────────────────────────────────────────────────────── */
  /** The single-column card that sits inline in a page. */
  card: string;
  /** Content column inside the embedded card. */
  cardInner: string;

  /* ── Popup surface ───────────────────────────────────────────────────────────── */
  /** The dialog body: sizing, radius, padding. */
  popupShell: string;
  /**
   * Added to `popupShell` only when an image rail is configured.
   *
   * Without this the shell would have to be permanently wide enough for a rail nobody
   * set, which is how a single-column popup ends up floating in a half-empty dialog.
   */
  popupShellWithAside: string;
  /** Row wrapper holding the image rail and the form column. */
  popupInner: string;
  /** The image rail. Rendered only when an image is configured. */
  popupAside: string;
  popupAsideImage: string;
  /** The form column beside the rail. */
  popupColumn: string;
  popupClose: string;
}

export interface LeadFormSkinDefinition {
  /** Matches `LEAD_FORM_SKIN` on the server; stored in `lead_form_config.template_key`. */
  id: string;
  name: string;
  /** One line, shown in the skin picker. Say what it LOOKS like, not what it does. */
  description: string;
  /** Which surfaces this skin is designed for. */
  surfaces: ("fixed" | "popup")[];
  /** The knobs offered to the CMS author, grouped for the settings form. */
  settingGroups: LeadFormSkinSettingGroup[];
  classes: LeadFormSkinClasses;
  /**
   * Stored values → CSS custom properties applied to the card.
   *
   * Every colour knob becomes a variable the class bundle already references, so a new
   * setting is one entry here and zero class edits.
   */
  cssVars: (values: LeadFormSkinValues) => CSSProperties;
  /**
   * Map a skin's OWN setting names onto the canonical keys the renderer reads.
   *
   * Skins that reproduce an existing hand-built variant should offer the author the key
   * names that variant already uses — someone who has configured lead popup 8 knows
   * `button_color`, `otp_title_text` and `desktop_bg_image`, and renaming them to this
   * file's vocabulary makes a familiar form unfamiliar for no gain. But `ConfigLeadForm`
   * reads the canonical names, so something has to bridge the two.
   *
   * This is that bridge, and it is ADDITIVE: it adds canonical keys alongside the skin's
   * own, never removing them, so the admin editor keeps reading and writing exactly the
   * keys it declared while the renderer sees what it expects. Skins whose settings already
   * use the canonical names simply omit this.
   */
  normalizeValues?: (values: LeadFormSkinValues) => LeadFormSkinValues;
  /**
   * Bind the area-of-interest question to the page's course, and lock it there.
   *
   * On a programme or specialisation page the course is not a question — the visitor is
   * already looking at the answer. Variants 7 and 8 (the IIT designs) therefore pin that
   * field to the page's course and disable it, which is what stops a lead arriving against
   * a programme the visitor never opened. Variant 9 deliberately does NOT: it runs on pages
   * that are about no course in particular, where the question is real.
   *
   * So this is per skin rather than global. A page with no course bound to it is unaffected
   * either way — there is nothing to pin the field to.
   */
  bindsCourseToPage?: boolean;
  /**
   * The ending renders INSIDE the shell — keeping the popup's sheet and hero rail, or the
   * inline card — instead of replacing it with a card of its own.
   *
   * Variant 8 does this on BOTH surfaces. `Leaddrop8` passes `embedded` to its thank-you as
   * a bare attribute (Leaddrop8.tsx:2573), so the thank-you is always the content-only
   * branch and the chrome always comes from the host: the dialog keeps its hero column, the
   * inline form keeps its lavender card, and only what sits beside/inside them changes.
   * Its `min-h-[420px] rounded-xl bg-white p-6` branch is dead code — matching it produces
   * a card nested inside the card, which is what this flag exists to avoid.
   *
   * Variant 9 is the opposite on purpose: its ending is a distinct card with a decoration
   * that bleeds past the top edge, which only works when it owns the whole surface.
   */
  endingKeepsShellChrome?: boolean;
  /**
   * The viewport width, in px, at which `popupAside` stops being a strip above the fields
   * and becomes the rail beside them.
   *
   * The renderer needs this as a NUMBER because it swaps the image FILE at that width —
   * `popup_mobile_image` below it, `popup_desktop_image` from it up — and a `<source
   * media>` query cannot be read out of the Tailwind class string that draws the same
   * boundary. Keep the two in step: if you move the `min-[…]` in `popupAside` that turns
   * the strip into a rail, move this with it, or the wide crop starts loading into the
   * short strip (or the reverse).
   *
   * Both skins today switch at 1080 (`sm` in this repo's redefined scale), which is the
   * default when a skin does not say.
   */
  popupAsideDesktopFrom?: number;
  /**
   * Optional decoration painted behind the terminal screen, before its content.
   *
   * A class bundle cannot express lead form 9's ending: a 460px gradient circle anchored
   * ABOVE the card, clipped by its overflow, so only the bottom arc shows. That is a
   * shape, not a style — it needs markup. Skins that want one return it here; skins that
   * do not simply omit this and get a plain card.
   */
  renderBackdrop?: (values: LeadFormSkinValues) => ReactNode;
}

/** Flatten a skin's groups — used to seed defaults and to validate an incoming payload. */
export const skinSettings = (skin: LeadFormSkinDefinition): LeadFormSkinSetting[] =>
  skin.settingGroups.flatMap((g) => g.settings);

/**
 * Every default the skin declares, as a values object.
 *
 * The renderer falls back to these, so a config saved before a setting existed renders
 * exactly as the developer intended rather than with a blank title or a transparent CTA.
 */
export const skinDefaults = (skin: LeadFormSkinDefinition): LeadFormSkinValues =>
  skinSettings(skin).reduce<Record<string, unknown>>((acc, s) => {
    if (s.defaultValue !== undefined) acc[s.key] = s.defaultValue;
    return acc;
  }, {}) as LeadFormSkinValues;

/**
 * Merge stored copy + theme over the skin's defaults.
 *
 * Order matters: defaults lose to whatever the author actually set, and an empty STRING
 * counts as "not set" — an author who clears a title wants the default back, not a form
 * with no heading.
 */
export const resolveSkinValues = (
  skin: LeadFormSkinDefinition,
  copyConfig?: Record<string, unknown> | null,
  themeConfig?: Record<string, unknown> | null,
): LeadFormSkinValues => {
  const out: Record<string, unknown> = { ...skinDefaults(skin) };
  for (const source of [copyConfig, themeConfig]) {
    if (!source || typeof source !== "object") continue;
    for (const [k, v] of Object.entries(source)) {
      if (v === undefined || v === null) continue;
      if (typeof v === "string" && v.trim() === "") continue;
      out[k] = v;
    }
  }
  const merged = out as LeadFormSkinValues;
  // Additive, and last: the skin's own names have already been merged, so the aliases are
  // computed from the author's final values rather than from the defaults.
  return skin.normalizeValues ? skin.normalizeValues(merged) : merged;
};

/**
 * Split a flat values object back into the two columns, per each setting's `store`.
 *
 * `extraSettings` carries settings that are not the skin's own — today the per-step
 * overrides generated from the form's schema (see ./stepSettings). It is a parameter
 * rather than something read off the skin because a key that nothing declares is a key
 * this function drops, and a setting the author filled in that silently fails to save is
 * the worst kind of bug: it looks like it worked until the page is reloaded.
 */
export const splitSkinValues = (
  skin: LeadFormSkinDefinition,
  values: LeadFormSkinValues,
  extraSettings: LeadFormSkinSetting[] = [],
): { copy_config: Record<string, unknown>; theme_config: Record<string, unknown> } => {
  const copy_config: Record<string, unknown> = {};
  const theme_config: Record<string, unknown> = {};
  for (const s of [...skinSettings(skin), ...extraSettings]) {
    const v = (values as Record<string, unknown>)[s.key];
    if (v === undefined) continue;
    (s.store === "copy" ? copy_config : theme_config)[s.key] = v;
  }
  return { copy_config, theme_config };
};
